import React, { useState, useEffect, useContext } from "react";
import useHTTP from "@/components/hooks/useHTTP";
import {
  getAppGuid,
  getContentIdFromContentUrl,
} from "@/components/elements/search/utility";
import { SchedulePushNotificationContext } from "@/context/SchedulePushNotificationContext";

//interfaces
import {
  IPublishedDetails,
  ICreateSchedulePush,
} from "@/interfaces/ScheduledNotifications";
import { IConfirmationModal } from "@/interfaces/IConfirmationModal";

//modals
import CreateNotificationModal from "@/components/modals/schedulePush/CreateNotificationModal";
import ConfirmationModal from "@/components/modals/ConfirmationModal";
import ModalWrapper from "@/components/modals/ModalWrapper";
import ViewNotificationHistoryModal from "../../modals/schedulePush/ViewNotificationHistoryModal";

//elements
import ToastNotification, {
  ToastMessageType,
} from "@/components/elements/global/ToastNotification";
import UpcomingScheduledNotification from "../../elements/schedulePush/UpcomingScheduledNotification";

//styling
import "../../../css/schedulePushNotifications/schedulePush.scss";
import { BasicButton } from "thrive-components";

const NotificationsManager = ({contentIsArchived = false}) => {
  const { get, post } = useHTTP();
  const { store, setStore } = useContext(SchedulePushNotificationContext);
  const [isContentIdSet, setIsContentIdSet] = useState<boolean>(false);
  const contentId = getContentIdFromContentUrl(window.location.href);

  const [openHistoryModal, setHistoryModal] = useState<boolean>(false);
  const [openCreateNotificationModal, setCreateNotificationModal] =
    useState<boolean>(false);
  const [openConfirmCancelModal, setConfirmCancelModal] =
    useState<boolean>(false);

  const [notificationWithoutHistory, setNotificationWithoutHistory] =
    useState<boolean>(true);
  const [isPublishDetailsDataLoaded, setPublishDetailsDataLoaded] =
    useState<boolean>(false);
  const [hasMetNotificationLimit, setHasMetNotificationLimit] =
    useState<boolean>(false);
  const [limitMessage, setLimitMessage] = useState<string>("");
  const [isCreatingNewNotification, setIsCreatingNewNotification] =
    useState<boolean>(false);
  const { clearStore } = useContext(SchedulePushNotificationContext);

  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastVariables, setToastVariables] = useState<{
    messageType?: ToastMessageType;
    message?: string;
  }>({ messageType: undefined, message: "" });

  const [publishDetails, setPublishDetails] = useState<IPublishedDetails>({
    hasNotificationHistory: false,
    id: null,
    appPublished: false,
    isPublished: false,
    listId: null,
    reach: null,
    scheduledArchiveDate: null,
    scheduledNotifications: [
      {
        id: null,
        scheduledPushTime: "",
      },
    ],
    scheduledPublishDate: null,
  });

  const [confirmationModalVariables, setConfirmationModalVariables] =
    useState<IConfirmationModal>({
      notificationId: null,
      modalClassName: "",
      modalTitle: "",
      confirmationQuestion: "",
      leftButton: "",
      rightButton: "",
    });

  //CI is important for integration to CMS
  function getNotifications(CI?: string) {
    if (contentId || CI) {
      setPublishDetailsDataLoaded(false);
      get(`notifications/content/${contentId || CI}/scheduledpushdetails`)
        .then(({ data }) => {
          setPublishDetails(data);
          // cms needs to know if there are scheduled notifications
          (window as any).hasScheduledNotifications =
            data.scheduledNotifications?.length >= 1;
        })
        .catch((error) => {
          console.error(error.message);
        })
        .finally(() => {
          setPublishDetailsDataLoaded(true);
        });
    }
  }

  //this is used in the CMS for logic regarding archival date of content
  useEffect(() => {
    (window as any).checkDoNotificationsExistAfterArchival = (
      archivalDate: string
    ) => {
      if (publishDetails) {
        publishDetails.scheduledNotifications.forEach((notification) => {
          if (
            new Date(notification.scheduledPushTime).getTime() >
            new Date(archivalDate).getTime()
          ) {
            (window as any).notificationsExistAfterArchival = true;
            return true;
          }
        });
      }
    };
  }, [publishDetails]);

  useEffect(() => {
    if (contentId) {
      setIsContentIdSet(true);
      getNotifications();
    }
  }, [contentId]);

  useEffect(() => {
    (window as any).refreshNotifications = (ci: string) =>
      getNotifications(ci || getContentIdFromContentUrl(window.location.href));
  }, []);

  useEffect(() => {
    setStore({
      ...store,
      contentId: Number(contentId),
      appGuid: getAppGuid(),
    });
  }, [contentId]);

  useEffect(() => {
    if (publishDetails?.hasNotificationHistory) {
      setNotificationWithoutHistory(false);
      checkNotificationLimit();
    }
  }, [isPublishDetailsDataLoaded]);

  //if the notification limit is reached, the user can no longer schedule future notifications - but they can send notifications now.
  //The notification limit is only calculated on future notifications, this means that when a scheduled notification has been sent,
  //the user can now schedule a new notification for the future.
  const NOTIFICATION_LIMIT = 5;
  const checkNotificationLimit = () => {
    let upcomingNotificationsCount = 0;
    publishDetails.scheduledNotifications.forEach((notification) => {
      if (
        notification.scheduledPushTime &&
        new Date(notification.scheduledPushTime).getTime() >
          new Date().getTime()
      ) {
        upcomingNotificationsCount++;
      }
    });

    if (upcomingNotificationsCount === NOTIFICATION_LIMIT) {
      setHasMetNotificationLimit(true);

      if (publishDetails.isPublished) {
        setLimitMessage(
          "5/5 notifications scheduled. You can choose to send a notification immediately."
        );
      } else {
        setLimitMessage("5/5 notifications scheduled.");
      }
    } else {
      setHasMetNotificationLimit(false);
    }
  };

  //only notifications which are scheduled for the future should be displayed in the Notification Manager
  function isScheduledForFuture(dateString: string) {
    const notificationDate = new Date(dateString);
    const currentDate = new Date();
    return currentDate.getTime() < notificationDate.getTime();

  }

  const handleOpenCreateNotificationClick = () => {
    setCreateNotificationModal(true);
  };

  const handleOpenHistoryModalClick = () => {
    setHistoryModal(true);
  };

  //when a user has navigated to Step 2 of the "create notification modal", show the "continue editing modal".
  //if the user has not yet navigated to Step 2, clear the store and all modals
  const onRequestCloseCreateNotificationModal = () => {
    if (isCreatingNewNotification) {
      setConfirmationModalVariables({
        modalClassName: `continue-editing-confirmation-modal`,
        modalTitle: "Continue Editing",
        confirmationQuestion:
          "Do you want to continue editing or disregard any unsaved changes?",
        leftButton: "Yes, Cancel",
        rightButton: "Continue Editing",
      });
      setConfirmCancelModal(true);
    } else {
      setCreateNotificationModal(false);
      clearStore();
    }
  };

  //when "continue editing modal" appears, and user wants to quit, close all modals, and reset store and modal relevant variables
  const onCancelCreateNotificationClick = () => {
    setConfirmCancelModal(false);
    setCreateNotificationModal(false);
    setIsCreatingNewNotification(false);
    clearStore();
  };

  //when "continue editing modal" appears and user wishes to continue editing, close the confirmation modal
  const onContinueEditingClick = () => {
    setConfirmCancelModal(false);
  };

  const onCreateNotificationClick = (schedulePush: ICreateSchedulePush) => {
    post(`notifications/schedulepush`, schedulePush)
      .then(() => {
        if (store.scheduledPushTime === "NOW") {
          setToastVariables({
            messageType: ToastMessageType.SUCCESS,
            message: "Your notification has been sent successfully.",
          });
        } else {
          setToastVariables({
            messageType: ToastMessageType.SUCCESS,
            message: "Notification successfully created",
          });
        }
        clearStore();
        setShowToast(true);
      })
      .catch((error) => {
        console.error(error.message);
        setToastVariables({
          messageType: ToastMessageType.WARNING,
          message: "Something went wrong. Please try again.",
        });
        setShowToast(true);
      })
      .finally(() => {
        setCreateNotificationModal(false);
      });
  };

  function disableCreateNotification() {
    return (!isPublishDetailsDataLoaded && !isContentIdSet) ||
        (!publishDetails.isPublished && hasMetNotificationLimit) ||
        contentIsArchived;
  }

  //reloading data after notification creation, edit and deletion
  useEffect(() => {
    getNotifications();
    checkNotificationLimit();
  }, [showToast]);

  //logic to determine if the functionality is available within the content
  function showNotificationManager() {
    return (
      (publishDetails.isPublished || publishDetails.scheduledPublishDate) &&
      publishDetails.appPublished
    );
  }
  if (!showNotificationManager()) {
    return <></>;
  }

  return (
    <div
      className="notifications-controls"
    >
      <h2 className="title">Notifications</h2>

      <div className="main-heading">
        <h3 className="heading">
          Create a notification for the audience of this item.
        </h3>
      </div>

      <div
        className="scheduled-notification-list"
      >
        {publishDetails.scheduledNotifications.map((items) =>
          //if an item has no scheduled push time it is because it has been cancelled and should not be shown
          items.scheduledPushTime &&
          isScheduledForFuture(items.scheduledPushTime) ? (
            <UpcomingScheduledNotification
              key={items.id}
              contentId={Number(contentId)}
              notificationId={items.id}
              publishDetails={publishDetails}
              scheduledPushTime={items.scheduledPushTime}
              setToastVariables={setToastVariables}
              setShowToast={setShowToast}
            />
          ) : null
        )}
      </div>

      {hasMetNotificationLimit && (
        <div id="limit-message" aria-label="5/5 notifications scheduled.">
          <label htmlFor="limit-message">{limitMessage}</label>
        </div>
      )}

      <BasicButton
        className="create-notifications-button"
        ariaLabel="Create a new notification"
        content={"Create Notification"}
        onButtonClick={handleOpenCreateNotificationClick}
        isDisabled={disableCreateNotification()}
      />

      <ModalWrapper
        isOpen={openCreateNotificationModal}
        onRequestClose={() => {
          onRequestCloseCreateNotificationModal();
        }}
        className="create-notification-modal"
      >
        <CreateNotificationModal
          contentId={Number(contentId)}
          publishDetails={publishDetails}
          onCreateNotificationClick={onCreateNotificationClick}
          setIsMidEditNotification={setIsCreatingNewNotification}
          hasMetNotificationLimit={hasMetNotificationLimit}
        />
      </ModalWrapper>

      <div className="view-notification-history divider-top" />

      <BasicButton 
        className="link-style-button notification-history-button"
        content={"View Notification History"} 
        ariaLabel={"View all past and upcoming scheduled notifications."}
        onButtonClick={handleOpenHistoryModalClick}
        isDisabled={notificationWithoutHistory}
      />

      <ModalWrapper
        isOpen={openHistoryModal}
        onRequestClose={() => setHistoryModal(false)}
        className="notification-history-modal"
      >
        <ViewNotificationHistoryModal
          contentId={Number(contentId)}
          totalItems={publishDetails.scheduledNotifications.length}
        />
      </ModalWrapper>

      <ModalWrapper
        isOpen={openConfirmCancelModal}
        onRequestClose={() => setConfirmCancelModal(false)}
        className="view-continue-editing-modal"
      >
        <ConfirmationModal
          confirmationModalVariables={confirmationModalVariables}
          onLeftButtonClick={onCancelCreateNotificationClick}
          onRightButtonClick={onContinueEditingClick}
          modalClassName={confirmationModalVariables.modalClassName}
        />
      </ModalWrapper>

      <ToastNotification
        show={showToast}
        closeToast={() => setShowToast(false)}
        messageType={toastVariables?.messageType}
      >
        {toastVariables?.message}
      </ToastNotification>
    </div>
  );
};

export default NotificationsManager;
